import { useState, useEffect } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { FONT } from "@shared/brand";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const { login } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [isLoading, setIsLoading] = useState(false);
  // VERIFY-LINK-1 (s123): a genuinely-invalid/unknown verification link lands here. Show a calm,
  // persistent notice with a resend affordance (below) rather than a one-shot destructive toast.
  const [showLinkError, setShowLinkError] = useState(false);

  // Surface verify-email outcomes redirected here by GET /api/auth/verify-email.
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("verified") === "true") {
      toast({ title: "Email verified!", description: "Sign in to continue." });
      try { localStorage.removeItem("pendingVerificationEmail"); } catch {}
    } else if (params.get("error") === "invalid_token") {
      // Softened (non-destructive): the common cause is a link already consumed by a mail scanner
      // (that case now routes to the "already verified" page — this branch is the rare truly-bad
      // link). The persistent notice below carries the resend action.
      setShowLinkError(true);
      toast({
        title: "That link didn't work",
        description: "It may have already been used or expired. You can send a fresh one below.",
      });
    }
  }, []); // run once on mount

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await login(data.email, data.password);
      if (result?.mfaRequired) {
        try {
          sessionStorage.setItem("mfaToken", result.mfaToken);
          sessionStorage.setItem("mfaPhoneLastFour", result.phoneLastFour ?? "");
        } catch {}
        setLocation("/verify-sms");
        return;
      }
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
      setLocation("/dashboard");
    } catch (error: any) {
      if (error?.emailVerificationRequired) {
        toast({
          title: "Verify your email",
          description: "Please verify your email address before signing in.",
        });
        setLocation(`/verify-email?email=${encodeURIComponent(data.email)}`);
        return;
      }
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="flex flex-col items-center w-full max-w-md">
        <div className="mb-3">
          <Link href="/">
            <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer inline-flex items-center gap-1" data-testid="link-back-home"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Back to home</span>
          </Link>
        </div>
        <AuthBrandHeader />
        {showLinkError && (
          <div
            className="w-full mb-4 rounded-md px-4 py-3 text-sm"
            style={{ backgroundColor: "#EEF2EB", color: "#3F5A41" }}
            data-testid="notice-link-invalid"
          >
            <p className="mb-2">
              That verification link didn't work — it may have already been used or expired.
            </p>
            <Link href="/verify-email">
              <span
                className="text-primary hover:underline cursor-pointer font-medium"
                data-testid="link-resend-verification"
              >
                Resend verification email
              </span>
            </Link>
          </div>
        )}
        <Card className="w-full">
          <CardHeader>
            <CardTitle data-testid="text-login-title" style={{ fontFamily: FONT.serif }}>Sign in</CardTitle>
            <CardDescription data-testid="text-login-description">
              Welcome back. Sign in to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="advisor@example.com"
                          data-testid="input-email"
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          {...field}
                          placeholder="Enter your password"
                          data-testid="input-password"
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end -mt-1">
                  <Link href="/forgot-password">
                    <span className="text-xs text-muted-foreground hover:text-primary cursor-pointer">
                      Forgot password?
                    </span>
                  </Link>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  data-testid="button-login"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link href="/signup">
                <span className="text-primary hover:underline cursor-pointer" data-testid="link-signup">
                  Sign up
                </span>
              </Link>
            </p>
          </CardFooter>
        </Card>
        <p className="text-center text-sm text-muted-foreground mt-4">
          Need help?{" "}
          <Link href="/support">
            <span className="text-primary hover:underline cursor-pointer" data-testid="link-support">
              Visit our support page
            </span>
          </Link>
        </p>
      </div>
    </div>
  );
}
